<template>
  <section class="ai-chats" aria-label="Your AI chats">
    <div class="ai-chats__heading">
      <div ref="sorting" class="ai-chats__title" @keydown.esc.stop.prevent="closeSort(true)" @keyup.esc.stop @focusout="handleSortFocusOut">
        <h3>Your chats</h3>
        <button
          ref="sortButton"
          class="ai-chats__sort-button"
          type="button"
          :title="`Sort by: ${sortName}`"
          :aria-label="`Sort chats. Current order: ${sortName}`"
          :aria-expanded="String(sortOpen)"
          aria-controls="ai-chat-sorting"
          :disabled="sortBusy || loading"
          @click="toggleSort"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
            <path d="M8 4v16m-4-4 4 4 4-4M14 5h6M14 10h4M14 15h2" />
          </svg>
        </button>
        <div v-if="sortOpen" id="ai-chat-sorting" class="ai-chats__sorting">
          <AppSelect
            ref="sortSelect"
            design="new"
            input-id="ai-chat-sort"
            aria-label="Sort chats by"
            :value="sort"
            :options="sortOptions"
            :reduce="option => option.value"
            :searchable="false"
            :clearable="false"
            :disabled="sortBusy || loading"
            @option:selecting="selectSort"
          />
        </div>
      </div>
      <AppButton design="new" theme="info" type="button" @click.native="$emit('new')">
        <span aria-hidden="true">+</span> New chat
      </AppButton>
    </div>
    <p v-if="!sessions.length" class="ai-chats__empty">{{ loading ? 'Loading chats…' : 'Your chats will appear here.' }}</p>
    <ul v-else class="ai-chats__list">
      <template v-for="(row, index) in sessions">
        <li
          v-if="index > 0 && chats[sessions[index - 1].id].isPinned && !chats[row.id].isPinned"
          :key="`divider-${row.id}`"
          class="ai-chats__divider"
          role="presentation"
          aria-hidden="true"
        ><hr></li>
        <ChatListItem
          :key="row.id"
          :row="row"
          :chat="chats[row.id]"
          :current="row.id === selected"
          :save="updateChat"
          @select="$emit('select', row.id)"
        />
      </template>
    </ul>
    <AppButton v-if="hasMore" class="ai-chats__more" design="new" theme="dark" type="button" :disabled="loading" @click.native="$emit('more')">
      {{ loading ? 'Loading…' : 'Load more chats' }}
    </AppButton>
  </section>
</template>

<script>
import ChatListItem from './ChatListItem'

export default {
  name: 'KnowledgeChatList',
  components: { ChatListItem },
  props: {
    sessions: { type: Array, required: true },
    chats: { type: Object, required: true },
    selected: { type: String, default: null },
    loading: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: false },
    sort: { type: String, default: 'activity' },
    sortBusy: { type: Boolean, default: false },
    updateChat: { type: Function, required: true }
  },
  data() {
    return {
      sortOpen: false,
      sortOptions: [
        { value: 'activity', name: 'Last activity' },
        { value: 'newest', name: 'Newest first' },
        { value: 'title', name: 'Name A–Z' }
      ]
    }
  },
  computed: {
    sortName() { return this.sortOptions.find(option => option.value === this.sort)?.name || 'Last activity' }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick, true)
  },
  methods: {
    toggleSort() {
      if (this.sortOpen) return this.closeSort(true)
      this.sortOpen = true
      document.addEventListener('click', this.handleOutsideClick, true)
      this.$nextTick(() => {
        const input = this.$refs.sortSelect?.$el.querySelector('input')
        if (input) input.focus()
      })
    },
    closeSort(restoreFocus = false) {
      this.sortOpen = false
      document.removeEventListener('click', this.handleOutsideClick, true)
      if (restoreFocus) this.$nextTick(() => { if (this.$refs.sortButton) this.$refs.sortButton.focus() })
    },
    handleOutsideClick(event) {
      if (!this.$refs.sorting.contains(event.target)) this.closeSort()
    },
    handleSortFocusOut(event) {
      if (event.relatedTarget && !this.$refs.sorting.contains(event.relatedTarget)) this.closeSort()
    },
    selectSort(option) {
      this.$emit('sort', option.value)
      this.closeSort(true)
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-chats { flex: 1; min-height: 0; overflow-y: auto; padding: 16px; background: $ui-surface-soft; }
.ai-chats__heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 16px; }
.ai-chats__heading h3 { margin: 0; font-size: 16px; font-weight: 600; }
.ai-chats__empty { margin: 12px 0 16px; font-size: 12px; color: $ui-muted; line-height: 1.5; }
.ai-chats__title { position: relative; display: flex; align-items: center; gap: 8px; }
.ai-chats__sort-button { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; padding: 0; border: 0; border-radius: 8px; background: transparent; color: $ui-muted; cursor: pointer; }
.ai-chats__sort-button:hover, .ai-chats__sort-button[aria-expanded="true"] { background: rgba($ui-blue, .08); color: $ui-blue; }
.ai-chats__sort-button:focus-visible { outline: 2px solid $ui-blue; outline-offset: 2px; }
.ai-chats__sort-button:disabled { opacity: .5; cursor: default; }
.ai-chats__sorting { position: absolute; top: calc(100% + 8px); left: 0; z-index: 12; width: 240px; max-width: calc(100vw - 56px); }
.ai-chats__list { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.ai-chats__divider { padding: 4px 0; }
.ai-chats__divider hr { margin: 0; border: 0; border-top: 1px solid $ui-line-strong; }
.ai-chats__more { margin: 16px auto 0; }
</style>
